<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Dynamic Sidebar -->
    <component 
      :is="currentLayout.sidebar" 
      v-model:isOpen="isSidebarOpen" 
    />

    <!-- 2. Main Content Wrapper -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Dynamic Topbar (Sticky) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <component 
          :is="currentLayout.topbar"
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Page Header -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">My Leave Requests</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your time off and track request status</p>
          </div>
          <button 
            @click="openNewRequestModal" 
            class="inline-flex items-center justify-center px-5 py-2.5 bg-[#035a76] hover:bg-[#024a61] text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            New Leave Request
          </button>
        </div>

        <!-- Leave Balance Summary -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div v-for="(balance, type) in leaveBalances" :key="type" class="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{{ type }} Leave</p>
                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {{ balance.remaining }}<span class="text-sm text-gray-400 font-normal"> / {{ balance.total }}</span>
                </p>
              </div>
              <div class="p-2 rounded-lg" :class="balance.remaining > 0 ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'">
                <svg v-if="balance.remaining > 0" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <svg v-else class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div class="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div class="h-1.5 rounded-full transition-all duration-500" 
                   :class="balance.remaining > 0 ? 'bg-green-500' : 'bg-red-500'"
                   :style="{ width: `${Math.max(0, (balance.remaining / balance.total) * 100)}%` }"></div>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Last used: <span class="font-medium">{{ balance.lastUsed || 'Never' }}</span>
            </p>
          </div>
        </div>

        <!-- Leave History Section -->
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Leave History</h3>
            <div class="flex gap-2">
              <select v-model="statusFilter" class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#035a76] focus:border-transparent">
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Leave Type</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Days</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Approver</th>
                  <th class="px-6 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr v-for="request in filteredHistory" :key="request.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-2.5 py-1 text-xs rounded-full font-medium text-white" :style="{ backgroundColor: getLeaveTypeColor(request.type) }">
                      {{ request.type }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {{ formatDate(request.startDate) }} – {{ formatDate(request.endDate) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {{ request.days }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="getStatusClass(request.status)" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize">
                      {{ request.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                    {{ request.approver || '—' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button @click="viewDetails(request)" class="text-[#035a76] dark:text-blue-400 hover:text-[#024a61] dark:hover:text-blue-300 mr-3 transition-colors">
                      View
                    </button>
                    <button v-if="request.status === 'pending'" @click="cancelRequest(request.id)" class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors">
                      Cancel
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredHistory.length === 0">
                  <td colspan="6" class="px-6 py-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No leave requests found matching your filters.</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>

    <!-- New Leave Request Modal -->
    <Transition name="modal">
      <div v-if="showNewRequestModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="showNewRequestModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col max-h-[90vh]">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">New Leave Request</h3>
            <button @click="showNewRequestModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div class="px-6 py-6 space-y-5 overflow-y-auto flex-1">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Leave Type *</label>
              <select v-model="newRequest.leave_type_id" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent">
                <option value="">Select leave type...</option>
                <option v-for="type in leaveTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
              </select>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Date Range *</label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                  <input v-model="newRequest.start_date" type="date" @change="calculateDuration"
                         class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent">
                </div>
                <div>
                  <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                  <input v-model="newRequest.end_date" type="date" @change="calculateDuration"
                         class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent">
                </div>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Duration</label>
              <input :value="newRequest.days > 0 ? `${newRequest.days} working day${newRequest.days !== 1 ? 's' : ''}` : 'Select dates above'" type="text" readonly 
                     class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed font-medium">
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Reason for Leave *</label>
              <textarea v-model="newRequest.reason" rows="3" required
                        class="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#035a76] focus:border-transparent resize-none"
                        placeholder="Please provide a brief explanation for your request..."></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Attachment (Optional)</label>
              <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-[#035a76] dark:hover:border-[#035a76] transition-colors cursor-pointer" @click="$refs.fileInput.click()">
                <div class="space-y-1 text-center">
                  <svg class="mx-auto h-10 w-10 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <div class="flex text-sm text-gray-600 dark:text-gray-400 justify-center">
                    <label class="relative cursor-pointer rounded-md font-medium text-[#035a76] dark:text-blue-400 hover:text-[#024a61] focus-within:outline-none">
                      <span>Upload a file</span>
                      <input ref="fileInput" type="file" class="sr-only" @change="handleFileUpload">
                    </label>
                    <p class="pl-1">or drag and drop</p>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-500">PDF, PNG, JPG up to 5MB</p>
                  <p v-if="newRequest.attachment" class="text-sm font-medium text-green-600 dark:text-green-400 mt-2">
                    Selected: {{ newRequest.attachment.name }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <button @click="showNewRequestModal = false" type="button" class="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
              Cancel
            </button>
            <button @click="submitRequest" type="button" :disabled="isSubmitting" class="px-5 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-[#035a76] hover:bg-[#024a61] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
              <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isSubmitting ? 'Submitting...' : 'Submit Request' }}
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Leave Details Modal -->
    <Transition name="modal">
      <div v-if="selectedRequest" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="selectedRequest = null">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Request Details</h3>
            <button @click="selectedRequest = null" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-6 py-6 space-y-4">
            <div class="flex justify-between items-start">
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Leave Type</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white text-lg">{{ selectedRequest.type }}</p>
              </div>
              <span :class="getStatusClass(selectedRequest.status)" class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize">
                {{ selectedRequest.status }}
              </span>
            </div>
            
            <div class="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Start Date</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDate(selectedRequest.startDate) }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">End Date</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDate(selectedRequest.endDate) }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Days</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedRequest.days }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Submitted On</p>
                <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ formatDate(selectedRequest.submittedOn) }}</p>
              </div>
            </div>

            <div class="pt-4 border-t border-gray-100 dark:border-gray-700">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Reason</p>
              <p class="mt-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {{ selectedRequest.reason }}
              </p>
            </div>

            <div v-if="selectedRequest.approver" class="pt-4 border-t border-gray-100 dark:border-gray-700">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Processed By</p>
              <p class="mt-1 font-medium text-gray-900 dark:text-white">{{ selectedRequest.approver }}</p>
            </div>

            <div v-if="selectedRequest.attachment" class="pt-4 border-t border-gray-100 dark:border-gray-700">
              <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Attachment</p>
              <a href="#" class="mt-2 inline-flex items-center text-sm font-medium text-[#035a76] dark:text-blue-400 hover:underline">
                <svg class="h-5 w-5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {{ selectedRequest.attachment }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/services/api';

// Dynamic Component Imports
import AcdSidebar from '@/components/acd/AcdSidebar.vue';
import AcdTopbar from '@/components/acd/AcdTopbar.vue';
import CommSidebar from '@/components/com/ComSidebar.vue';
import CommTopbar from '@/components/com/ComTopbar.vue';
import ItSidebar from '@/components/it/ItSidebar.vue';
import ItTopbar from '@/components/it/ItTopbar.vue';
import AdministrationSidebar from '@/components/administration/AdministrationSidebar.vue';
import AdministrationTopbar from '@/components/administration/AdministrationTopbar.vue';

const route = useRoute();

// Layout Mapping
const layoutMap = {
  academics: { sidebar: AcdSidebar, topbar: AcdTopbar },
  it: { sidebar: ItSidebar, topbar: ItTopbar },
  com: { sidebar: CommSidebar, topbar: CommTopbar },
  administration: { sidebar: AdministrationSidebar, topbar: AdministrationTopbar },
  procurement: { sidebar: AdministrationSidebar, topbar: AdministrationTopbar }
};

const currentModule = computed(() => {
  if (route.path.startsWith('/academics')) return 'academics';
  if (route.path.startsWith('/it')) return 'it';
  if (route.path.startsWith('/com')) return 'com';
  if (route.path.startsWith('/procurement')) return 'procurement';
  if (route.path.startsWith('/administration')) return 'administration';
  return 'administration';
});

const currentLayout = computed(() => layoutMap[currentModule.value] || layoutMap.administration);

// State
const isSidebarOpen = ref(false);
const isDarkMode = ref(false);
const currentPageTitle = ref('Leave Management');
const statusFilter = ref('all');
const showNewRequestModal = ref(false);
const selectedRequest = ref(null);
const isSubmitting = ref(false);

const leaveTypes = ref([]);
const leaveHistory = ref([]);
const leaveBalances = ref({});

const newRequest = ref({
  leave_type_id: '',
  start_date: '',
  end_date: '',
  days: 0,
  reason: '',
  attachment: null
});

// ✅ IMPROVED: Get current logged-in user ID with multiple fallbacks
const currentUserId = computed(() => {
  const storedUser = localStorage.getItem('user');
  if (!storedUser) {
    console.warn('No user found in localStorage');
    return null;
  }
  
  try {
    const user = JSON.parse(storedUser);
    console.log('Current user object:', user); // Debug log
    
    // Try multiple possible field names for the user ID
    const userId = user.applicant_id || 
                   user.id || 
                   user.user_id || 
                   user.employee_id ||
                   user.applicantId ||
                   user.userId;
    
    if (!userId) {
      console.error('User ID not found in user object. Available keys:', Object.keys(user));
      return null;
    }
    
    return String(userId);
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
});

// Fetch Data from API
const fetchData = async () => {
  if (!currentUserId.value) {
    console.error('Cannot fetch data: User ID is not available');
    return;
  }

  try {
    const [typesRes, leavesRes] = await Promise.all([
      api.get('LeaveTypes.php'),
      api.get('Leaves.php')
    ]);

    leaveTypes.value = typesRes.data || [];
    
    const allLeaves = leavesRes.data?.data || [];
    console.log('All leaves from API:', allLeaves);
    console.log('Filtering for user ID:', currentUserId.value);
    
    // Filter leaves for the current logged-in employee only
    leaveHistory.value = allLeaves
      .filter(leave => {
        const leaveApplicantId = String(leave.applicant_id);
        const currentId = String(currentUserId.value);
        console.log(`Comparing leave applicant_id (${leaveApplicantId}) with current user (${currentId})`);
        return leaveApplicantId === currentId;
      })
      .map(leave => ({
        id: leave.id,
        type: leave.leave_type_name,
        startDate: leave.start_date,
        endDate: leave.end_date,
        days: calculateDays(leave.start_date, leave.end_date),
        status: leave.status,
        approver: leave.approved_by,
        submittedOn: leave.created_at,
        reason: leave.reason,
        attachment: leave.attachment_path || leave.attachment
      }))
      .sort((a, b) => new Date(b.submittedOn) - new Date(a.submittedOn));

    console.log('Filtered leave history:', leaveHistory.value);
    calculateBalances();

  } catch (error) {
    console.error('Error fetching leave data:', error);
  }
};

const calculateBalances = () => {
  const balances = {};
  leaveTypes.value.forEach(type => {
    const total = type.default_days || 21; 
    const used = leaveHistory.value
      .filter(l => l.type === type.name && l.status === 'approved')
      .reduce((sum, l) => sum + l.days, 0);
    
    const lastUsedRecord = leaveHistory.value
      .filter(l => l.type === type.name && l.status === 'approved')
      .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))[0];

    balances[type.name] = {
      total: total,
      remaining: Math.max(0, total - used),
      lastUsed: lastUsedRecord ? formatDate(lastUsedRecord.startDate) : 'Never'
    };
  });
  leaveBalances.value = balances;
};

// Computed
const filteredHistory = computed(() => {
  if (statusFilter.value === 'all') return leaveHistory.value;
  return leaveHistory.value.filter(req => req.status === statusFilter.value);
});

// Methods
const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add('dark');
    localStorage.theme = 'dark';
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.theme = 'light';
  }
};

const openNewRequestModal = () => {
  if (!currentUserId.value) {
    alert('Unable to identify user. Please log in again.');
    return;
  }
  newRequest.value = { leave_type_id: '', start_date: '', end_date: '', days: 0, reason: '', attachment: null };
  showNewRequestModal.value = true;
};

const calculateDuration = () => {
  if (newRequest.value.start_date && newRequest.value.end_date) {
    newRequest.value.days = calculateDays(newRequest.value.start_date, newRequest.value.end_date);
  } else {
    newRequest.value.days = 0;
  }
};

const calculateDays = (start, end) => {
  if (!start || !end) return 0;
  const diffTime = Math.abs(new Date(end) - new Date(start));
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const handleFileUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }
    newRequest.value.attachment = { name: file.name, file: file };
  }
};

const submitRequest = async () => {
  if (!currentUserId.value) {
    alert('Unable to identify user. Please log in again.');
    console.error('Submission failed: currentUserId is null');
    return;
  }

  if (!newRequest.value.leave_type_id || !newRequest.value.start_date || !newRequest.value.end_date || !newRequest.value.reason) {
    alert('Please fill in all required fields.');
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    let response;
    
    // ✅ FIX: Use JSON if no attachment, FormData only if there's a file
    if (!newRequest.value.attachment) {
      // Regular JSON POST (same as HR section)
      const payload = {
        applicant_id: currentUserId.value,
        leave_type_id: newRequest.value.leave_type_id,
        start_date: newRequest.value.start_date,
        end_date: newRequest.value.end_date,
        reason: newRequest.value.reason
      };

      console.log('Submitting leave request (JSON):', payload);
      response = await api.post('Leaves.php', payload);
    } else {
      // FormData POST only when there's a file
      const formData = new FormData();
      formData.append('applicant_id', currentUserId.value);
      formData.append('leave_type_id', newRequest.value.leave_type_id);
      formData.append('start_date', newRequest.value.start_date);
      formData.append('end_date', newRequest.value.end_date);
      formData.append('reason', newRequest.value.reason);
      formData.append('attachment', newRequest.value.attachment.file);

      console.log('Submitting leave request (FormData):', {
        applicant_id: currentUserId.value,
        leave_type_id: newRequest.value.leave_type_id,
        start_date: newRequest.value.start_date,
        end_date: newRequest.value.end_date,
        reason: newRequest.value.reason,
        attachment: newRequest.value.attachment.name
      });

      // ✅ Don't set Content-Type - let axios handle it
      response = await api.post('Leaves.php', formData);
    }

    if (response.data.success) {
      alert('Leave request submitted successfully!');
      showNewRequestModal.value = false;
      fetchData();
    } else {
      alert(response.data.message || 'Failed to submit request.');
    }
  } catch (error) {
    console.error('Submit error:', error);
    console.error('Error response:', error.response?.data);
    alert('An error occurred while submitting the request.');
  } finally {
    isSubmitting.value = false;
  }
};


const viewDetails = (request) => {
  selectedRequest.value = request;
};

const cancelRequest = async (id) => {
  if (!confirm('Are you sure you want to cancel this pending request?')) return;
  
  try {
    const response = await api.post('Leaves.php', {
      action: 'update_status',
      id: id,
      status: 'cancelled',
      approved_by: 'Employee (Self)'
    });
    
    if (response.data.success) {
      alert('Request cancelled successfully.');
      fetchData();
    } else {
      alert(response.data.message || 'Failed to cancel request.');
    }
  } catch (error) {
    console.error('Cancel error:', error);
    alert('An error occurred while cancelling the request.');
  }
};

const getStatusClass = (status) => {
  const map = {
    pending: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    approved: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    rejected: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  };
  return map[status] || 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

const getLeaveTypeColor = (type) => {
  if (!type) return '#6b7280';
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = type.charCodeAt(i) + ((hash << 5) - hash);
  const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

onMounted(() => {
  if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDarkMode.value = true;
    document.documentElement.classList.add('dark');
  }
  
  // ✅ DEBUG: Log user data on mount
  console.log('=== LEAVE PAGE MOUNTED ===');
  console.log('User in localStorage:', localStorage.getItem('user'));
  console.log('Current User ID:', currentUserId.value);
  
  fetchData();
});
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: all 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; transform: scale(0.95); }
</style>